import { useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router"
import api from "../../lib/api"
import { toastError } from "../../lib/toast"
import ModuleLayout from "../../components/admin/ModuleLayout"
import { btnPrimary, btnSecondary } from "../../components/admin/FormComponents"
import QuestionInput from "./section-quiz-test/QuestionInput"
import { TYPE_LABEL, buildAnswers, isAnswered, type StartedRun } from "./section-quiz-test/model"

// Probar un examen como lo vería un estudiante — openspec/changes/section-quiz-expert-test.
// Solo existe en Gestión. La prueba vive aparte de los intentos reales: no cuenta en
// estadísticas, tiempo de espera ni completitud.

export default function SectionQuizTestPage() {
  const { sectionQuizId } = useParams<{ sectionQuizId: string }>()
  const navigate = useNavigate()
  const [jevAvailable, setJevAvailable] = useState<boolean | null>(null)
  const [run, setRun] = useState<StartedRun | null>(null)
  const [starting, setStarting] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [values, setValues] = useState<Record<number, unknown>>({})

  useEffect(() => {
    api.get("/academy/section-quizzes/test/config")
      .then((res) => setJevAvailable(Boolean(res.data.data?.jev_available)))
      .catch(() => setJevAvailable(false))
  }, [])

  const start = async () => {
    if (!sectionQuizId || starting) return
    setStarting(true)
    try {
      const res = await api.post("/academy/section-quizzes/test/runs", { section_quiz_id: sectionQuizId })
      setRun(res.data.data)
      setValues({})
    } catch (err) {
      toastError(err)
    } finally {
      setStarting(false)
    }
  }

  const answeredCount = useMemo(
    () => (run ? run.questions.filter((q) => isAnswered(q.question_type, values[q.index], q.content)).length : 0),
    [run, values]
  )

  const submit = async () => {
    if (!run || submitting) return
    setSubmitting(true)
    try {
      await api.post(`/academy/section-quizzes/test/runs/${run.id}/submit`, { answers: buildAnswers(run.questions, values) })
      navigate(`/instructor/section-quizzes/tests/${run.id}`)
    } catch (err) {
      toastError(err)
      setSubmitting(false)
    }
  }

  return (
    <ModuleLayout
      breadcrumbs={[{ label: "SateLab", to: "/" }, "Contenido", { label: "Exámenes IA", to: "/instructor/section-quizzes" }, "Probar examen"]}
      title="Probar examen"
      subtitle="Responde el examen como lo haría un estudiante. Después verás cómo lo calificó cada calificador y podrás darnos tu feedback."
      actions={
        <>
          <button type="button" className={btnSecondary} onClick={() => navigate(`/instructor/section-quizzes/${sectionQuizId}/tests`)}>Historial de pruebas</button>
          <button type="button" className={btnSecondary} onClick={() => navigate("/instructor/section-quizzes")}>Volver</button>
        </>
      }
      tabs={[]}
      metrics={run ? [
        { label: "Preguntas", value: String(run.questions.length) },
        { label: "Respondidas", value: `${answeredCount} / ${run.questions.length}` },
        { label: "Origen", value: run.source === "live" ? "Generado ahora" : "Banco de respaldo" },
      ] : []}
    >
      {!run && (
        <div className="max-w-2xl space-y-4">
          <ul className="list-disc space-y-1.5 pl-5 text-sm text-gray-700 dark:text-gray-300">
            <li>Las preguntas se generan igual que para un estudiante y pueden tardar hasta un minuto.</li>
            <li><strong>Esta prueba no afecta datos reales</strong>: no cuenta en estadísticas, no inicia tiempo de espera ni completa cursos.</li>
            <li>Verás la calificación del calificador actual y la de Jev lado a lado, y si Jev respalda la clave con el texto de la lección.</li>
          </ul>

          {jevAvailable === false && (
            <p className="rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:bg-amber-500/10 dark:text-amber-200" role="status">
              Jev no está configurado en este entorno: la prueba se calificará solo con el calificador actual.
            </p>
          )}

          <button type="button" className={btnPrimary} onClick={() => void start()} disabled={starting}>
            {starting ? "Generando preguntas…" : "Empezar la prueba"}
          </button>
        </div>
      )}

      {run && (
        <form
          className="max-w-3xl"
          onSubmit={(e) => { e.preventDefault(); void submit() }}
          aria-label="Respuestas de la prueba"
        >
          <ol className="space-y-8">
            {run.questions.map((q) => (
              <li key={q.index} className="border-b border-gray-100 pb-8 last:border-0 dark:border-gray-800">
                <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Pregunta {q.index + 1} · {TYPE_LABEL[q.question_type] ?? q.question_type}
                </p>
                <p className="mb-3 whitespace-pre-line text-base font-medium text-gray-900 dark:text-white">{q.question_text}</p>
                <QuestionInput question={q} value={values[q.index]} onChange={(v) => setValues((prev) => ({ ...prev, [q.index]: v }))} />
              </li>
            ))}
          </ol>

          <div className="sticky bottom-0 mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-white/90 py-3 backdrop-blur dark:border-gray-800 dark:bg-gray-900/90">
            <p className="text-sm text-gray-600 dark:text-gray-300" role="status">
              {answeredCount === run.questions.length
                ? "Respondiste todas las preguntas."
                : `${run.questions.length - answeredCount} pregunta(s) sin responder: se contarán como incorrectas.`}
            </p>
            <button type="submit" className={btnPrimary} disabled={submitting}>
              {submitting ? "Calificando…" : "Enviar y calificar"}
            </button>
          </div>
        </form>
      )}
    </ModuleLayout>
  )
}
